import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, useMediaQuery, useTheme, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import PropTypes from 'prop-types';

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const savedAssistantState = localStorage.getItem('assistantOpen');
    if (savedAssistantState) {
      setAssistantOpen(JSON.parse(savedAssistantState));
    }
  }, []);

  // Update currentPath when location changes
  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  // React Router will automatically trigger re-renders when location changes

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarOpen = (open) => {
    setIsOpen(open);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleAssistant = () => {
    const newState = !assistantOpen;
    setAssistantOpen(newState);
    localStorage.setItem('assistantOpen', JSON.stringify(newState));
  };

  // Determine if current route needs special handling - use clean path without query params
  const cleanPath = currentPath.split('?')[0];
  const isChatRoute = cleanPath.includes('/chats');
  const isCalendarRoute = cleanPath.includes('/booking');
  const isStockRoute = cleanPath.includes('/car_stock');
  const isClientRoute = cleanPath.includes('/clients');
  const needsSpecialPadding = isChatRoute || isCalendarRoute || isStockRoute || isClientRoute;

  // Clean up any persisting chat state when navigating away from chat routes
  useEffect(() => {
    if (!isChatRoute) {
      // Remove any chat-specific body attributes
      document.body.removeAttribute('data-in-conversation');
      // Ensure scroll is re-enabled by resetting any inline styles
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      // Force a reflow to ensure changes are applied
      document.body.offsetHeight;
    }
  }, [location, currentPath, isChatRoute]); // Include location as dependency

  // For chat routes, we want full-screen behavior on mobile only when inside a conversation
  // Check if we're in a specific conversation (URL has chat ID) vs just on the chat list
  const isInConversation = isChatRoute && (
    (currentPath.includes('/chats/') && 
     currentPath.split('/chats/')[1] && 
     currentPath.split('/chats/')[1] !== '') ||
    document.body.hasAttribute('data-in-conversation') // Also check the data attribute set by Chats component
  );
  const isChatFullScreen = isInConversation && isMobile; // Only hide header when IN a conversation on mobile

  // Force re-render when conversation state changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setCurrentPath(window.location.pathname + Date.now()); // Force re-render
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-in-conversation']
    });
    
    return () => observer.disconnect();
  }, []);

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      position: 'relative',
      // Only hide overflow for chat routes, allow scrolling for other pages
      overflow: isChatRoute ? 'hidden' : 'visible',
      background: {
        xs: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        md: 'radial-gradient(1200px 600px at 10% -10%, #ecfeff 0%, transparent 40%), radial-gradient(1200px 800px at 100% 10%, #f0fdf4 0%, transparent 40%), linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
      },
      // Force hide webapp header when in conversation mode
      ...(isChatFullScreen && {
        '& .MuiAppBar-root': {
          display: 'none !important',
        },
      }),
    }}>
      {/* Hide header on mobile for full-screen chat */}
      {!isChatFullScreen && (
        <Header 
          toggleSidebar={handleDrawerToggle}
          isSidebarOpen={isOpen}
          darkMode={darkMode}
          assistantOpen={assistantOpen}
          toggleAssistant={toggleAssistant}
        />
      )}
      <Box sx={{ 
        display: 'flex',
        flex: 1,
        position: 'relative',
        pt: isChatFullScreen ? 0 : { xs: '64px', md: '80px' }, // Remove header offset for full-screen chat
        // Only hide overflow for chat routes, allow scrolling for other pages
        overflow: isChatRoute ? 'hidden' : 'visible'
      }}>
        {/* Hide sidebar for full-screen chat on mobile */}
        {!isChatFullScreen && (
          <Sidebar 
            mobileOpen={mobileOpen} 
            handleDrawerToggle={handleDrawerToggle}
            isOpen={isOpen}
            handleSidebarOpen={handleSidebarOpen}
            darkMode={darkMode}
          />
        )}
        
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            maxWidth: '100%',
            // Different overflow strategies for mobile vs desktop
            overflowY: isChatRoute ? 'hidden' : 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            touchAction: isChatRoute ? 'none' : 'pan-y',
            height: isChatRoute && isMobile ? '100vh' : 'auto',
            // Padding ottimizzato - molto ridotto
            p: isChatRoute ? (isMobile ? 0 : { xs: 0, md: 1.5, lg: 2 }) : (
              isMobile ? { xs: 1.5, sm: 2 } : (needsSpecialPadding ? { xs: 2, md: 2.5, lg: 3 } : { xs: 2, sm: 2.5, md: 3 })
            ),
            // Nessun margin-left, la sidebar è flottante
            ml: 0,
            mr: isChatFullScreen ? 0 : { 
              xs: 0,
              md: assistantOpen ? '400px' : 0
            },
            transition: theme.transitions.create(['margin', 'width'], {
              duration: theme.transitions.duration.leavingScreen,
            }),
            position: 'relative',
            // Mobile-specific chat spacing
            ...(isChatRoute && isMobile && {
              paddingBottom: 0,
              height: '100vh',
              maxHeight: '100vh',
            }),
            // Desktop-specific chat container
            ...(isChatRoute && !isMobile && {
              height: 'calc(100vh - 96px)', // Aggiustato per nuovo header
              maxHeight: 'calc(100vh - 96px)',
              overflow: 'hidden',
            }),
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Layout;